import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { initializeApp } from 'firebase/app';
import { getDatabase, ref, onValue, set, onChildAdded, onChildRemoved, onDisconnect, query, orderByChild, startAt, get } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { environment } from '../../../../environments/environment';

export interface FirebaseRealtimeEvent {
  type: 'message' | 'typing' | 'read' | 'presence' | 'message_removed';
  roomId: string;
  messageId?: string;
  userId?: string;
  data?: any;
  timestamp: number;
}

export interface FirebaseChatSessionSummary {
  studentId: string;
  otherUserId: string;
  roomKey: string;
  sessionId?: string;
  lastMessage?: string;
  lastMessageAt?: number;
  lastMessageBy?: string;
  userType?: string;
  metadata?: any;
}

@Injectable({
  providedIn: 'root'
})
export class FirebaseService {
  private app = initializeApp(environment.firebase);
  private database = getDatabase(this.app);
  private storage = getStorage(this.app);
  private enablePresence = false;

  private connectionStatusSubject = new BehaviorSubject<boolean>(false);
  public connectionStatus$ = this.connectionStatusSubject.asObservable();

  private currentUserId: string | null = null;
  private currentRoomId: string | null = null;
  private listeners: { [key: string]: () => void } = {};

  constructor(private ngZone: NgZone) {
    // Monitor connection status
    const connectedRef = ref(this.database, '.info/connected');
    onValue(connectedRef, (snap: any) => {
      if (snap.val() === true) {
        this.connectionStatusSubject.next(true);
      } else {
        this.connectionStatusSubject.next(false);
      }
    });
  }

  // Initialize Firebase for a specific room
  initializeRoom(userId: string, roomId: string) {
    this.currentUserId = userId;
    this.currentRoomId = roomId;
    if (this.enablePresence) {
      this.setUserOnline(userId);
    }
  }

  // Real-time listeners for room events
  listenToRoom(roomId: string, sinceTimestamp?: number | string): Observable<FirebaseRealtimeEvent> {
    return new Observable((observer) => {
      if (!roomId) return;
      
      // Listen for new messages
      const messagesRef = ref(this.database, `chat_sessions/${roomId}/messages`);
      const startTimestamp = this.normalizeTimestamp(sinceTimestamp);
      const messagesQuery = startTimestamp
        ? query(messagesRef, orderByChild('timestamp'), startAt(startTimestamp + 1))
        : query(messagesRef, orderByChild('timestamp'));
      const messageUnsubscribe = onChildAdded(messagesQuery, (snapshot: any) => {
        const messageData = snapshot.val();
        this.ngZone.run(() => {
          observer.next({
            type: 'message',
            roomId,
            messageId: snapshot.key,
            data: messageData,
            timestamp: Date.now()
          });
        });
      });
      const messageRemovedUnsubscribe = onChildRemoved(messagesRef, (snapshot: any) => {
        this.ngZone.run(() => {
          observer.next({
            type: 'message_removed',
            roomId,
            messageId: snapshot.key,
            data: snapshot.val(),
            timestamp: Date.now()
          });
        });
      });

      // Listen for typing indicators
      const typingRef = ref(this.database, `typing/${roomId}`);
      const typingUnsubscribe = onValue(typingRef, (snapshot: any) => {
        snapshot.forEach((childSnapshot: any) => {
          const typingData = childSnapshot.val();
          this.ngZone.run(() => {
            observer.next({
              type: 'typing',
              roomId,
              userId: childSnapshot.key,
              data: typingData,
              timestamp: Date.now()
            });
          });
        });
      });

      // Listen for read receipts
      const readsRef = ref(this.database, `reads/${roomId}`);
      const readsUnsubscribe = onChildAdded(readsRef, (snapshot: any) => {
        const readData = snapshot.val();
        this.ngZone.run(() => {
          observer.next({
            type: 'read',
            roomId,
            messageId: readData.messageId,
            userId: readData.userId,
            data: readData,
            timestamp: Date.now()
          });
        });
      });

      // Store listeners for cleanup
      this.listeners[`messages_${roomId}`] = messageUnsubscribe;
      this.listeners[`messages_removed_${roomId}`] = messageRemovedUnsubscribe;
      this.listeners[`typing_${roomId}`] = typingUnsubscribe;
      this.listeners[`reads_${roomId}`] = readsUnsubscribe;

      return () => {
        messageUnsubscribe();
        messageRemovedUnsubscribe();
        typingUnsubscribe();
        readsUnsubscribe();
        delete this.listeners[`messages_${roomId}`];
        delete this.listeners[`messages_removed_${roomId}`];
        delete this.listeners[`typing_${roomId}`];
        delete this.listeners[`reads_${roomId}`];
      };
    });
  }

  // Listen to direct messages in both mirrored chat session branches:
  // chat_sessions/senderId/receiverId/messages and chat_sessions/receiverId/senderId/messages
  listenToDirectMessages(currentUserId: string, otherUserId: string, sinceTimestamp?: number | string): Observable<FirebaseRealtimeEvent> {
    return new Observable((observer) => {
      if (!currentUserId || !otherUserId) {
        return;
      }
      console.log(`Listening to direct messages between ${currentUserId} and ${otherUserId} since ${sinceTimestamp}`);
      const conversationId = `${currentUserId}_${otherUserId}`;
      const attachMessageListeners = (senderId: string, receiverId: string, direction: 'outgoing' | 'incoming') => {
        const pathKey = `${senderId}_${receiverId}`;
        const messagesPath = `chat_sessions/${senderId}/${receiverId}/messages`;
        const messagesRef = ref(this.database, messagesPath);
        const messagesQuery = messagesRef;

        get(messagesRef)
          .then((snapshot) => {
            console.info('[Firebase Direct Chat] Path snapshot', {
              path: messagesPath,
              exists: snapshot.exists(),
              childCount: snapshot.size,
              value: snapshot.val()
            });
          })
          .catch((error) => {
            console.error('[Firebase Direct Chat] Failed to read path', {
              path: messagesPath,
              code: error?.code,
              message: error?.message,
              error
            });
            this.ngZone.run(() => observer.error(error));
          });

        const messageUnsubscribe = onChildAdded(messagesQuery, (snapshot: any) => {
          const snapshotValue = snapshot.val();
          const messageData = this.normalizeMessageData(snapshotValue);
          console.info('[Firebase Direct Chat] Message received', {
            path: `${messagesPath}/${snapshot.key}`,
            direction,
            messageData
          });
          this.ngZone.run(() => {
            observer.next({
              type: 'message',
              roomId: conversationId,
              messageId: `${pathKey}_${snapshot.key}`,
              userId: senderId,
              data: {
                ...messageData,
                sender_id: messageData?.sender_id ?? senderId,
                receiver_id: messageData?.receiver_id ?? receiverId,
                direction
              },
              timestamp: Date.now()
            });
          });
        }, (error) => {
          const firebaseError = error as any;
          console.error('[Firebase Direct Chat] onChildAdded cancelled', {
            path: messagesPath,
            code: firebaseError?.code,
            message: firebaseError?.message,
            error
          });
          this.ngZone.run(() => observer.error(error));
        });

        const removedUnsubscribe = onChildRemoved(messagesRef, (snapshot: any) => {
          this.ngZone.run(() => {
            observer.next({
              type: 'message_removed',
              roomId: conversationId,
              messageId: `${pathKey}_${snapshot.key}`,
              userId: senderId,
              data: snapshot.val(),
              timestamp: Date.now()
            });
          });
        }, (error) => {
          const firebaseError = error as any;
          console.error('[Firebase Direct Chat] onChildRemoved cancelled', {
            path: messagesPath,
            code: firebaseError?.code,
            message: firebaseError?.message,
            error
          });
        });

        this.listeners[`direct_messages_${pathKey}`] = messageUnsubscribe;
        this.listeners[`direct_messages_removed_${pathKey}`] = removedUnsubscribe;

        return () => {
          messageUnsubscribe();
          removedUnsubscribe();
          delete this.listeners[`direct_messages_${pathKey}`];
          delete this.listeners[`direct_messages_removed_${pathKey}`];
        };
      };

      const detachOutgoing = attachMessageListeners(currentUserId, otherUserId, 'outgoing');
      const detachIncoming = attachMessageListeners(otherUserId, currentUserId, 'incoming');

      return () => {
        detachOutgoing?.();
        detachIncoming?.();
      };
    });
  }

  listenToChatSessionsForUser(currentUserId: string): Observable<FirebaseChatSessionSummary[]> {
    return new Observable((observer) => {
      if (!currentUserId) {
        observer.next([]);
        return;
      }

      const chatSessionsRef = ref(this.database, 'chat_sessions');
      const unsubscribe = onValue(chatSessionsRef, (snapshot: any) => {
        const sessions: FirebaseChatSessionSummary[] = [];

        snapshot.forEach((studentSnapshot: any) => {
          const studentId = studentSnapshot.key;
          studentSnapshot.forEach((userSnapshot: any) => {
            const userId = userSnapshot.key;
            const value = userSnapshot.val() ?? {};

            if (studentId !== currentUserId && userId !== currentUserId) {
              return;
            }

            const otherUserId = studentId === currentUserId ? userId : studentId;
            let lastMessageAt = this.normalizeTimestamp(value.last_message_at ?? value.lastMessageAt ?? value.timestamp ?? null);
            let lastMessage = value.last_message ?? value.lastMessage ?? '';
            let lastMessageBy = value.last_message_by ?? value.lastMessageBy ?? value.message_by ?? value.sender_type ?? undefined;

            if (value.messages && typeof value.messages === 'object') {
              let latestMessageTimestamp = lastMessageAt ?? 0;
              let latestMessageText = lastMessage;
              let latestMessageBy = lastMessageBy;

              Object.keys(value.messages).forEach((messageKey) => {
                const messageData = value.messages[messageKey] ?? {};
                const messageTimestamp = this.normalizeTimestamp(
                  messageData.timestamp ?? messageData.message_date ?? messageData.created_at ?? messageData.updated_at ?? null
                ) ?? 0;

                if (messageTimestamp >= latestMessageTimestamp) {
                  latestMessageTimestamp = messageTimestamp;
                  latestMessageText = this.resolveMessageText(messageData);
                  latestMessageBy = messageData.sender_type ?? messageData.message_by ?? latestMessageBy;
                }
              });

              lastMessageAt = latestMessageTimestamp || lastMessageAt;
              lastMessage = latestMessageText || lastMessage;
              lastMessageBy = latestMessageBy || lastMessageBy;
            }

            sessions.push({
              studentId,
              otherUserId,
              roomKey: `${studentId}_${userId}`,
              sessionId: value.session_id?.toString?.() ?? value.session_id ?? value.id?.toString?.(),
              lastMessage,
              lastMessageAt: lastMessageAt ?? undefined,
              lastMessageBy,
              userType: value.user_type ?? value.userType ?? undefined,
              metadata: value
            });
          });
        });

        sessions.sort((a, b) => {
          const first = a.lastMessageAt ?? 0;
          const second = b.lastMessageAt ?? 0;
          return second - first;
        });

        observer.next(sessions);
      }, (error: any) => {
        this.ngZone.run(() => observer.error(error));
      });

      return () => unsubscribe();
    });
  }

  listenToUnseenMessagesForUser(currentUserId: string): Observable<Record<string, number>> {
    return new Observable((observer) => {
      if (!currentUserId) {
        observer.next({});
        return;
      }

      let chatSessionsValue: any = {};
      let seenValue: any = {};

      const emitUnseenCounts = () => {
        const unseenCounts: Record<string, number> = {};

        Object.keys(chatSessionsValue ?? {}).forEach((studentId) => {
          const studentSessions = chatSessionsValue?.[studentId] ?? {};
          Object.keys(studentSessions).forEach((userId) => {
            const session = studentSessions[userId] ?? {};

            if (studentId !== currentUserId && userId !== currentUserId) {
              return;
            }

            const otherUserId = studentId === currentUserId ? userId : studentId;
            const messages = session.messages;
            if (!messages || typeof messages !== 'object') {
              return;
            }

            Object.keys(messages).forEach((messageKey) => {
              const messageData = this.normalizeMessageData(messages[messageKey]);
              const senderId = messageData.sender_id?.toString?.()
                ?? messageData.user_id?.toString?.()
                ?? (messageData.sender_type === 'student' ? studentId : userId);

              if (senderId === currentUserId) {
                return;
              }

              if (
                !this.isMessageSeenByUser(messageData, currentUserId) &&
                !this.hasSeenMessage(seenValue, currentUserId, studentId, userId, messageKey, session)
              ) {
                unseenCounts[otherUserId] = (unseenCounts[otherUserId] ?? 0) + 1;
              }
            });
          });
        });

        this.ngZone.run(() => observer.next(unseenCounts));
      };

      const chatSessionsRef = ref(this.database, 'chat_sessions');
      const seenRef = ref(this.database, 'seen');
      const chatSessionsUnsubscribe = onValue(chatSessionsRef, (snapshot: any) => {
        chatSessionsValue = snapshot.val() ?? {};
        emitUnseenCounts();
      }, (error: any) => {
        this.ngZone.run(() => observer.error(error));
      });
      const seenUnsubscribe = onValue(seenRef, (snapshot: any) => {
        seenValue = snapshot.val() ?? {};
        emitUnseenCounts();
      }, (error: any) => {
        this.ngZone.run(() => observer.error(error));
      });

      return () => {
        chatSessionsUnsubscribe();
        seenUnsubscribe();
      };
    });
  }

  // Emit message event (called after backend saves message)
  emitMessageEvent(roomId: string | number | null, messageId: string, messageData: any): Promise<void> {
    return new Promise((resolve, reject) => {
      const messageRef = ref(this.database, `chat_sessions/${roomId}/messages/${messageId}`);
      set(messageRef, {
        ...messageData,
        timestamp: Date.now()
      })
        .then(() => resolve())
        .catch(reject);
    });
  }

  // Typing status management
  setTypingStatus(roomId: string, userId: string, isTyping: boolean): Promise<void> {
    return new Promise((resolve, reject) => {
      const typingRef = ref(this.database, `typing/${roomId}/${userId}`);
      set(typingRef, {
        isTyping,
        timestamp: Date.now()
      })
        .then(() => resolve())
        .catch(reject);
    });
  }

  // Online presence management
  setUserOnline(userId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const presenceRef = ref(this.database, `presence/${userId}`);
      const connectedRef = ref(this.database, '.info/connected');

      onValue(connectedRef, (snap: any) => {
        if (snap.val() === true) {
          // Set user as online
          set(presenceRef, {
            online: true,
            lastSeen: Date.now()
          });

          // Set up disconnect handler
          onDisconnect(presenceRef).set({
            online: false,
            lastSeen: Date.now()
          });
        }
      }, { onlyOnce: true });

      resolve();
    });
  }

  // Emit read receipt
  emitReadReceipt(roomId: string, messageId: string, userId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const readRef = ref(this.database, `reads/${roomId}/${messageId}_${userId}`);
      set(readRef, {
        messageId,
        userId,
        timestamp: Date.now()
      })
        .then(() => resolve())
        .catch(reject);
    });
  }

  // Emit seen receipt
  emitSeenReceipt(roomId: string, messageId: string, userId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const seenRef = ref(this.database, `seen/${roomId}/${messageId}_${userId}`);
      set(seenRef, {
        messageId,
        userId,
        timestamp: Date.now()
      })
        .then(() => resolve())
        .catch(reject);
    });
  }

  async markConversationMessagesAsSeen(currentUserId: string, otherUserId: string, roomId?: string | null): Promise<void> {
    if (!currentUserId || !otherUserId) {
      return;
    }

    const writes: Promise<void>[] = [];
    const attachWritesForPath = async (senderId: string, receiverId: string) => {
      const messagesPath = `chat_sessions/${senderId}/${receiverId}/messages`;
      const messagesSnapshot = await get(ref(this.database, messagesPath));

      if (!messagesSnapshot.exists()) {
        return;
      }

      messagesSnapshot.forEach((messageSnapshot: any) => {
        const messageKey = messageSnapshot.key?.toString?.();
        if (!messageKey) {
          return;
        }

        const messageData = this.normalizeMessageData(messageSnapshot.val());
        const messageSenderId = messageData.sender_id?.toString?.()
          ?? messageData.user_id?.toString?.()
          ?? senderId;

        if (messageSenderId === currentUserId || this.isMessageSeenByUser(messageData, currentUserId)) {
          return;
        }

        const now = Date.now();
        const pathKey = `${senderId}_${receiverId}`;
        const roomKeys = [
          roomId?.toString?.(),
          `${currentUserId}_${otherUserId}`,
          `${otherUserId}_${currentUserId}`
        ].filter((key): key is string => !!key);
        const messageIds = [
          messageKey,
          `${pathKey}_${messageKey}`
        ];

        writes.push(set(ref(this.database, `${messagesPath}/${messageKey}/read_by/${currentUserId}`), {
          read: true,
          userId: currentUserId,
          timestamp: now
        }));

        roomKeys.forEach((roomKey) => {
          messageIds.forEach((messageId) => {
            writes.push(set(ref(this.database, `seen/${roomKey}/${messageId}_${currentUserId}`), {
              messageId,
              userId: currentUserId,
              timestamp: now
            }));
          });
        });
      });
    };

    await Promise.all([
      attachWritesForPath(currentUserId, otherUserId),
      attachWritesForPath(otherUserId, currentUserId)
    ]);
    await Promise.all(writes);
  }

  // File upload to Firebase Storage
  uploadFileToStorage(file: File, path: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const fileRef = storageRef(this.storage, `${path}/${Date.now()}_${file.name}`);
      uploadBytes(fileRef, file)
        .then((snapshot) => getDownloadURL(snapshot.ref))
        .then(resolve)
        .catch(reject);
    });
  }

  // Get online users
  getOnlineUsers(): Observable<{ userId: string; online: boolean; lastSeen: number }[]> {
    return new Observable((observer) => {
      const presenceRef = ref(this.database, 'presence');

      const unsubscribe = onValue(presenceRef, (snapshot: any) => {
        const onlineUsers: { userId: string; online: boolean; lastSeen: number }[] = [];
        snapshot.forEach((childSnapshot: any) => {
          const presenceData = childSnapshot.val();
          if (presenceData.online) {
            onlineUsers.push({
              userId: childSnapshot.key,
              online: presenceData.online,
              lastSeen: presenceData.lastSeen
            });
          }
        });
        observer.next(onlineUsers);
      });

      return () => unsubscribe();
    });
  }

  private normalizeTimestamp(timestamp?: number | string): number | null {
    if (timestamp === undefined || timestamp === null || timestamp === '') {
      return null;
    }

    if (typeof timestamp === 'number') {
      return Number.isFinite(timestamp) ? timestamp : null;
    }

    const numericValue = Number(timestamp);
    if (Number.isFinite(numericValue)) {
      return numericValue;
    }

    const parsedDate = Date.parse(timestamp);
    return Number.isNaN(parsedDate) ? null : parsedDate;
  }

  private normalizeMessageData(messageData: any): any {
    if (messageData && typeof messageData === 'object') {
      return messageData;
    }

    return {
      message: messageData ?? '',
      timestamp: Date.now()
    };
  }

  private resolveMessageText(messageData: any): string {
    if (!messageData || typeof messageData !== 'object') {
      return String(messageData ?? '');
    }

    const rawMessage = messageData.message ?? messageData.text ?? messageData.body ?? messageData.content ?? messageData.test;
    if (typeof rawMessage === 'string') {
      return rawMessage;
    }

    return JSON.stringify(rawMessage ?? '');
  }

  private isMessageSeenByUser(messageData: any, userId: string): boolean {
    const readEntry = messageData?.read_by?.[userId] ?? messageData?.seen_by?.[userId];
    if (readEntry === true) {
      return true;
    }

    return !!(readEntry && typeof readEntry === 'object' && (readEntry.read || readEntry.seen || readEntry.userId?.toString?.() === userId));
  }

  private hasSeenMessage(
    seenValue: any,
    currentUserId: string,
    studentId: string,
    userId: string,
    messageKey: string,
    session: any
  ): boolean {
    const roomKeys = [
      `${studentId}_${userId}`,
      `${userId}_${studentId}`,
      session?.session_id?.toString?.(),
      session?.id?.toString?.()
    ].filter(Boolean);
    const messageIds = [
      messageKey,
      `${studentId}_${userId}_${messageKey}`,
      `${userId}_${studentId}_${messageKey}`
    ];

    return roomKeys.some((roomKey) => {
      const roomSeen = seenValue?.[roomKey];
      if (!roomSeen || typeof roomSeen !== 'object') {
        return false;
      }

      return Object.values(roomSeen).some((seenEntry: any) =>
        seenEntry?.userId?.toString?.() === currentUserId &&
        messageIds.includes(seenEntry?.messageId?.toString?.())
      );
    });
  }

  // Cleanup and disconnect
  disconnect() {
    // Clean up all listeners
    Object.values(this.listeners).forEach(unsubscribe => unsubscribe());
    this.listeners = {};

    // Set user offline
    if (this.enablePresence && this.currentUserId) {
      const presenceRef = ref(this.database, `presence/${this.currentUserId}`);
      set(presenceRef, {
        online: false,
        lastSeen: Date.now()
      });
    }

    this.currentUserId = null;
    this.currentRoomId = null;
    this.connectionStatusSubject.next(false);
  }
}
